import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  VStack,
  Container,
} from "@chakra-ui/react";
import { Element } from "@prismicio/react";
import { useLocation } from "@reach/router";

import { AnimatePresence } from "framer-motion";
import { FC, Fragment } from "react";
import { BlockContainer, IconBox, MotionBox, ShiIcon } from "src/atoms";
import Renderer from "src/lib/renderer";
import { MIGRATION_PATHS } from "src/templates/constants";
import { hyperlink } from "../../lib/renderer/elements/hyperlink";
import { image } from "../../lib/renderer/elements/image";

const BlockFaq: FC<Gatsby.PageBlockFaqFragment> = ({ primary, items }) => {
  const { pathname } = useLocation();
  if (!primary || !items) throw Error();

  const { is_large: isLarge } = primary;
  const isMigrationPage = MIGRATION_PATHS.includes(pathname);

  const renderBox = () => {
    return (
      <Box
        sx={{
          ".chakra-divider": {
            marginX: "auto",
          },
        }}
      >
        <Renderer field={primary.content} />
      </Box>
    );
  };

  const renderAccordion = (isLarge?: boolean) => {
    const paddingRightProps = isLarge ? { paddingRight: { md: "0" } } : {};
    return (
      <Accordion
        id="faq"
        defaultIndex={isMigrationPage ? [2] : []}
        textAlign="left"
        width="100%"
        allowToggle
        allowMultiple
      >
        {items.map((item) => {
          if (!item) return null;

          return (
            <AccordionItem key={item.question} borderColor="border-primary">
              {({ isExpanded }) => (
                <Fragment>
                  <AccordionButton
                    display="flex"
                    justifyContent="space-between"
                    paddingY="space-24"
                    textAlign="left"
                    paddingX={{ md: "0" }}
                    {...paddingRightProps}
                  >
                    <Heading as="h5" variant="heading-5">
                      {item.question}
                    </Heading>

                    <IconBox
                      bg="secondary-stone-100"
                      position="relative"
                      borderRadius="intermediate"
                    >
                      <AnimatePresence>
                        <MotionBox
                          position="absolute"
                          key={isExpanded ? "minus" : "plus"}
                          initial={{ opacity: 0, rotate: 60 }}
                          animate={{ opacity: 1, rotate: 0 }}
                          exit={{ display: "none" }}
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <ShiIcon
                            name={isExpanded ? "minus" : "plus"}
                            color="text-primary"
                          />
                        </MotionBox>
                      </AnimatePresence>
                    </IconBox>
                  </AccordionButton>

                  <AccordionPanel
                    color="text-primary"
                    sx={{
                      fontSize: "font-15",
                      lineHeight: "base",
                      fontWeight: "normal",
                      letterSpacings: "semitight",
                      paddingLeft: { md: "0" },
                      ul: {
                        marginLeft: "space-32",
                      },
                      p: {
                        fontSize: "font-15",
                        lineHeight: "base",
                        fontWeight: "normal",
                        letterSpacings: "semitight",
                        _last: {
                          marginBottom: 0,
                        },
                      },
                      a: {
                        color: "text-primary",
                      },
                    }}
                  >
                    <Renderer
                      field={item.answer}
                      overrides={
                        isMigrationPage
                          ? {
                              [Element.hyperlink]: (args: any) =>
                                hyperlink({
                                  ...args,
                                  overrideProps: {
                                    textDecoration: "underline",
                                  },
                                }),
                              [Element.image]: (args: any) =>
                                image({
                                  ...args,
                                  overrideProps: {
                                    useOriginal: true,
                                  },
                                }),
                            }
                          : {}
                      }
                    />
                  </AccordionPanel>
                </Fragment>
              )}
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  if (isLarge) {
    return (
      <BlockContainer>
        <VStack spacing={{ base: "space-24", md: "space-40" }} width="full">
          <Container variant="heading">{renderBox()}</Container>
          {renderAccordion(isLarge)}
        </VStack>
      </BlockContainer>
    );
  }

  return (
    <BlockContainer>
      <Container variant="heading">
        <VStack spacing="space-24" width="100%">
          {renderBox()}
          {renderAccordion(isLarge)}
        </VStack>
      </Container>
    </BlockContainer>
  );
};

export default BlockFaq;
